import {Step, StepButton, Stepper, StepperProps, styled, Typography, useTheme} from "@mui/material";
import {LightChevronRight} from "../utils/icons";
import React from "react";
import {Close, Done, Warning} from "@mui/icons-material";

interface StepIconProps {
    step: React.ReactNode;
    isActive?: boolean;
    status?: string;
    iconstyles?: React.CSSProperties;
    stepstyles?: React.CSSProperties;
}

const StepIcon = ({step, isActive, status, iconstyles, stepstyles}: StepIconProps) => {
    const theme = useTheme();

    if (status === "success") {
        return (
            <div
                style={{
                    position: "relative",
                    width: iconstyles?.width ?? "20px",
                    height: iconstyles?.height ?? "20px",
                    padding: "10px",
                    borderRadius: "100px",
                    border: `1px solid ${theme.palette.tertiary.main}`,
                    backgroundColor: theme.palette.tertiary.main
                }}>

                <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: "100%",
                    display: 'flex',
                    justifyContent: "center",
                    alignItems: "center",
                    left: 0,
                    top: 0,
                    color: "white",
                }}>
                    <Done style={{color: "white"}}/>
                </div>
            </div>
        )
    } else if (status === "error") {
        return (
            <div
                style={{
                    position: "relative",
                    width: iconstyles?.width ?? "20px",
                    height: iconstyles?.height ?? "20px",
                    padding: "10px",
                    borderRadius: "100px",
                    border: `1px solid #EE3224`,
                    backgroundColor: "#EE3224"
                }}>

                <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: "100%",
                    display: 'flex',
                    justifyContent: "center",
                    alignItems: "center",
                    left: 0,
                    top: 0,
                    color: "white",
                }}>
                    <Close style={{color: "white"}}/>
                </div>
            </div>
        )
    }
    else if (status === "warning") {
        return (
            <div
                style={{
                    position: "relative",
                    width: iconstyles?.width ?? "20px",
                    height: iconstyles?.height ?? "20px",
                    padding: "10px",
                    borderRadius: "100px",
                    border: `1px solid #FFB822`,
                    backgroundColor: "#FFB822"
                }}>

                <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: "100%",
                    display: 'flex',
                    justifyContent: "center",
                    alignItems: "center",
                    left: 0,
                    top: 0,
                    color: "white",
                }}>
                    <Warning style={{color: "white", width: "20px", height: "20px"}}/>
                </div>
            </div>
        )
    }

    return (
        <div
            style={{
                position: "relative",
                width: iconstyles?.width ?? "20px",
                height: iconstyles?.height ?? "20px",
                padding: "10px",
                borderRadius: "100px",
                border: `1px solid ${isActive ? theme.palette.tertiary.main : "#CECECE"}`,
            }}>

            <div style={{
                width: '100%',
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: `${isActive ? theme.palette.tertiary.main : "#CECECE"}`,
            }}>{step}</div>
        </div>
    )
};

const StyledStepper = styled(Stepper)(() => ({
    width: "100%",
    "& .MuiStepConnector-vertical": {
        padding: "8px 0"
    },
    "& .MuiStepLabel-labelContainer": {
        textAlign: "left"
    }
}));

const StyledStep = styled(Step)(({ theme }) => ({
    "& .MuiStepIcon-root.MuiStepIcon-active": {
        color: theme.palette.tertiary?.main || theme.palette.primary.main,
    }
}));

interface StepData {
    label?: React.ReactNode;
    status?: string;
}

interface EnhancedStepperProps extends Omit<StepperProps, "children" | "activeStep"> {
    steps: StepData[];
    activeStep: number;
    vertical?: boolean;
    onStepClick?: (index: number) => void;
    iconstyles?: React.CSSProperties;
    stepstyles?: React.CSSProperties;
}

export default function EnhancedStepper(props: EnhancedStepperProps) {
    const {vertical} = props;

    const theme = useTheme();

    return (
        <StyledStepper
            nonLinear
            activeStep={props.activeStep}
            connector={vertical ? undefined : <LightChevronRight/>}
            orientation={vertical ? "vertical" : "horizontal"}
            {...props}
        >
            {props.steps.map((step, index) => {
                return (
                    <StyledStep key={index}
                    >
                        <StepButton
                            style={{cursor: props.onStepClick ? "pointer" : "default"}}
                            disableRipple
                            onClick={props.onStepClick ? () => props.onStepClick(index) : undefined}
                            icon={
                                <StepIcon
                                    step={index + 1} isActive={index === props.activeStep}
                                    status={step.status}
                                    iconstyles={props.iconstyles}
                                />
                            }
                        >
                            <Typography
                                style={{
                                    fontWeight: 600,
                                    color: `${index === props.activeStep
                                        ? theme.palette.tertiary.main
                                        : index === props.activeStep
                                            ? "black"
                                            : "#CECECE"}`,
                                    ...props.stepstyles
                                }}
                            >
                                {step.label}
                            </Typography>
                        </StepButton>
                    </StyledStep>
                );
            })}
        </StyledStepper>
    )
}
